import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Hero = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("landing");

  return (
    <section className="relative min-h-[90vh] overflow-hidden bg-gradient-to-br from-[#1A1F2C] to-[#2D1A43]">
      {/* Background video wrapper with fixed positioning */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        <div className="absolute inset-0 w-full h-full scale-125">
          <iframe 
            src="https://app.videas.fr/embed/media/8c98648e-0758-4a30-a68a-37793cde6230/?title=false&logo=false&thumbnail_duration=false&controls=false&autoplay=true&loop=true&thumbnail=video" 
            frameBorder="0" 
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" 
            allowFullScreen 
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} 
            referrerPolicy="unsafe-url"
            title="Background video"
          ></iframe>
        </div>
      </div>

      {/* Gradient overlays */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-full h-full bg-gradient-to-br from-[#1A1F2C]/90 to-[#2D1A43]/90"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/30 to-secondary/30 mix-blend-overlay animate-gradient-flow"></div>
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/20 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px]"></div>
      </div>
      
      <div className="container relative mx-auto px-4 h-[90vh] flex items-center justify-center">
        <motion.div 
          className="max-w-3xl mx-auto text-center text-white space-y-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1 
            className="text-4xl lg:text-6xl font-bold leading-tight bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
          >
            {t("hero.title")}
          </motion.h1>
          
          <motion.p 
            className="text-xl text-gray-300 leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
          >
            {t("hero.subtitle")}
          </motion.p>
          
          <motion.div 
            className="mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary-600 text-white px-12 py-7 rounded-lg text-xl h-auto shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300" 
              onClick={() => navigate("/request-demo")}
            >
              {t("hero.cta")} <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </motion.div>
          
          <motion.div 
            className="mt-10 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          >
            <div className="flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/10">
              <Globe className="w-5 h-5 text-primary-200 mr-2" />
              <span className="text-gray-200">
                24+ {t('languages')}
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
